'use client';

import { useState, useId, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { MessageCircle, Loader2, Check } from 'lucide-react';
import { LANDING_CONTENT } from '../constants/content';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import { readStoredAttribution } from '@/lib/tracking/attribution';
import { trackEvent } from '@/lib/tracking/engagement';
import { attachFormAnalytics } from '@/lib/tracking/form-analytics';
import { StagedCtaCard, StagedContinueButton } from './WhatsAppDemoFormStaged';
import { DemoSuccessCard } from './WhatsAppDemoFormSuccess';
import {
  PAPER_BAND,
  PAPER_INK,
  PAPER_RULE,
  PAPER_VARIANT,
} from '@/features/marketing/ui/paper-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '@/features/marketing/ui/cta-recipe';
import {
  type DemoFormSuccessData,
  type DemoStep,
  type FocusTarget,
  type WhatsAppDemoFormProps,
  CONTINUE_LABEL,
  validateDemoFormFields,
  validateContactStepFields,
  mapDemoServerError,
  isAbortError,
  getDemoInputBorderClass,
  submitDemoFormRequest,
  getSuccessOverlayClass,
  buildNoscriptHtml,
  useFormStartTracking,
  STATIC_WA_URL,
} from './whatsapp-demo-form-helpers';

/**
 * WhatsApp Demo Form — Phase 2.
 *
 * Public, anonymous: visitor submits name + phone + GDPR consent.
 * Calls POST /api/marketing/whatsapp-demo.
 * Flag-gated: rendered by SolutionSection only when
 *   NEXT_PUBLIC_WHATSAPP_DEMO_ENABLED === 'true'.
 *
 * State machine (Addendum C §C3, 2026-07-08 rebuild; 2026-07-10 fleet rebuild
 * LANE L3 removed the touch-device auto-navigate — Judge 2 forensic finding 3:
 * "Testing the bot ejects the highest-intent mobile visitor from the site"):
 *   idle → filling (focus rings, blur validation) → submitting (spinner +
 *   botSection.form.submitting, pointer-events-none, zero layout shift) →
 *   SUCCESS: the form crossfades out 200ms and is replaced IN-PLACE (same
 *   bounding box — absolute overlay over the still-mounted, inert form) by a
 *   house-recipe success card (2026-07-10 round 2: neutral #121418/border
 *   card + amber check, no green — green stays reserved for the WhatsApp-
 *   native bezel elsewhere on the page) with TWO explicit, persistent
 *   actions — "Deschide WhatsApp" (opens waUrl) and "Începe trialul CRM"
 *   (registers with intent=both, preserving the CRM path for a visitor who
 *   just tested the bot). No auto-navigate on any device; the visitor always
 *   chooses. | error: inline recoverable message above the submit button,
 *   values persist, no toast.
 *
 * A11y: label/aria pairs, aria-invalid, aria-describedby for errors,
 *       visually-hidden honeypot (aria-hidden, tabIndex=-1).
 * Styling: glass input pattern (dark bg, amber focus ring, amber fill button
 *          with near-black label). Glass checkbox: sr-only real <input> +
 *          visual peer box (neutral house fill + light check when checked;
 *          amber is a CTA-only decision color, round 2 design ruling).
 * Animation: respects prefers-reduced-motion on the success state.
 * Fetch: AbortController tied to component lifetime; AbortError is silently
 *        ignored (normal unmount / dock-morph lifecycle).
 *
 * R5-P3 LANE L5 (design amendment §6 "BOT — staged interactive flow",
 * Decision 9): `staged` prop (additive, does NOT touch `compact`) renders a
 * 3-step machine INSIDE the SAME `<form>`/fields/handlers above: step 0 CTA
 * card -> step 1 name+phone (+ "Continuă") -> step 2 language+consent+Send.
 * All 3 rows stay ALWAYS mounted on one persistent grid (`AnimatePresence`/
 * conditional-mount BANNED — would remount inputs and lose focus/cursor/
 * autofill, r5-lib-audit.md §3); inactive rows get the native `inert` prop
 * (already proven on the success overlay below) — never unmounted, so DOM
 * node identity survives every step change (ADV-21).
 * A11y (ADV-20): validate-before-advance; step changes move focus to the
 * new step's control (`pendingFocus` state + one effect); a submit-time
 * error owned by an INERT step re-opens it and focuses the OWNING field;
 * the language radiogroup implements the full ARIA keyboard pattern.
 * No-JS (ADV-11): this form is React-only — a no-JS visitor sees it but
 * cannot submit, staged or not; a `<noscript>` block ships a working
 * `wa.me` link as a parallel escape hatch. PRM gets the staged flow with
 * INSTANT step swaps (PRM ≠ no-JS).
 * Pure logic/types: `whatsapp-demo-form-helpers.ts`; staged-only UI atoms:
 * `WhatsAppDemoFormStaged.tsx` (both split out for the 800-line cap). R7
 * paper flip (2026-07-17): dark-tokens.ts -> paper-tokens.ts throughout;
 * error TEXT (not borders/rings) red-400 -> red-600 for AA on paper.
 *
 * Related: [[knowledge/ui-components]] [[rules/glass-styling]] [[rules/ui-quality]]
 */

// WhatsAppDemoFormProps moved to whatsapp-demo-form-helpers.ts with the rest
// of the form's pure types (component-file-large P2, 800-line cap).

export default function WhatsAppDemoForm({
  compact = false,
  staged = false,
}: WhatsAppDemoFormProps) {
  const { locale, t } = useLocale();
  // Field labels/placeholders/errors stay at whatsappWedge.demoForm (LIVE compat
  // contract); the §C3 state-machine strings live at botSection.form.
  const { demoForm } = LANDING_CONTENT.whatsappWedge;
  const botForm = LANDING_CONTENT.botSection.form;
  // Step-0 CTA card + no-JS fallback copy (R5-P3 content-surgeon addition,
  // content.ts `botSection.staged.{cta,line,noscript}`) — read directly here
  // (same pattern as `botForm` above) rather than threaded through
  // SolutionSection, since this IS the component that renders step 0.
  const stagedCopy = LANDING_CONTENT.botSection.staged;
  // Success card's second action reuses the CTA dictionary's canonical "Compact
  // CRM CTA" copy (nav.cta.primary, "Începe trialul CRM") rather than inventing
  // new wording — content.ts is Lane L0's file; this is the closest existing
  // canonical string to Judge 2 finding 3's named fix ("Testează CRM-ul 14
  // zile"), flagged for L0/Review as a follow-up content addition.
  const { primary: crmTrialCta } = LANDING_CONTENT.nav.cta;

  // ── Language picker — initialized ONCE from locale (Rule #19: no useEffect reset).
  // demoLang is the source of truth once the user has seen the form; locale changes
  // (e.g. from a parent toggle) do NOT snap the user's explicit selection back.
  // demoLang is sent as `lang` in the POST body → routes to the correct approved
  // WhatsApp Business template for that language.
  const [demoLang, setDemoLang] = useState<'ro' | 'en'>(locale);

  // ── Form fields ───────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<DemoFormSuccessData | null>(null);
  // Two-phase flag driving the 200ms success crossfade: the overlay mounts at
  // opacity-0, then a rAF flips this so the transition actually runs (same
  // rAF-off-the-commit pattern as StickyMobileCTA — avoids set-state-in-effect).
  const [successVisible, setSuccessVisible] = useState(false);
  useEffect(() => {
    if (!success) return;
    const rafId = requestAnimationFrame(() => setSuccessVisible(true));
    return () => cancelAnimationFrame(rafId);
  }, [success]);

  // ── AbortController — tied to component lifetime ──────────────────────────
  // One controller per component mount; abort fires on unmount so in-flight
  // fetch is cancelled (dock morph unmount race → double-submit / dead-tree write).
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // ── Engagement + per-field form-funnel telemetry (v2 contract 3 + v3 Lane COLLECT) ──
  // handleFirstFieldFocus: once-per-mount 'form_start' (extracted, 800L cap). attachFormAnalytics below: per-field focus/abandon — never reads field VALUES.
  const handleFirstFieldFocus = useFormStartTracking('demo_whatsapp');
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (!formRef.current) return;
    return attachFormAnalytics(formRef.current, 'whatsapp_demo');
  }, []);

  // ── Stable id prefix for aria associations ────────────────────────────────
  const uid = useId();
  const nameId = `${uid}-name`;
  const phoneId = `${uid}-phone`;
  const consentId = `${uid}-consent`;
  const nameErrorId = `${uid}-name-error`;
  const phoneErrorId = `${uid}-phone-error`;
  const consentErrorId = `${uid}-consent-error`;
  const langGroupId = `${uid}-lang`;

  // ── Staged flow state (R5-P3 LANE L5) — additive, inert when !staged ──────
  const [step, setStep] = useState<DemoStep>('cta');
  // Decoupled from `step`: lets a submit-time error target the EXACT owning
  // field (ADV-20) instead of just "this step's first control".
  const [pendingFocus, setPendingFocus] = useState<FocusTarget>(null);

  const ctaButtonRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const roLangButtonRef = useRef<HTMLButtonElement>(null);
  const enLangButtonRef = useRef<HTMLButtonElement>(null);
  const consentInputRef = useRef<HTMLInputElement>(null);

  /** Advances the step machine AND queues which control gets focus once the
   * newly active row's `inert` clears (the effect below runs post-render). */
  function goToStep(next: DemoStep, focus: FocusTarget) {
    setStep(next);
    setPendingFocus(focus);
  }

  // Moves focus once, after the DOM reflects the new step — never on the
  // very first mount (autofocusing the below-the-fold step-0 CTA on page
  // load would yank scroll position, ui-quality.md "autoFocus sparingly").
  useEffect(() => {
    if (!staged || pendingFocus === null) return;
    const el =
      pendingFocus === 'cta'
        ? ctaButtonRef.current
        : pendingFocus === 'name'
          ? nameInputRef.current
          : pendingFocus === 'phone'
            ? phoneInputRef.current
            : pendingFocus === 'lang'
              ? (demoLang === 'ro' ? roLangButtonRef : enLangButtonRef).current
              : consentInputRef.current;
    el?.focus();
    setPendingFocus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- demoLang is read intentionally only to pick ro/en at focus time, not to re-trigger the effect on every language toggle.
  }, [pendingFocus, staged]);

  function handleCtaPress() {
    goToStep('contact', 'name');
  }

  /** ADV-20 "validate-before-advance": name+phone gate the step1->step2
   * advance; consent isn't visible yet at this point, so it is never part of
   * this check (it's validated at final submit, where it lives). */
  function handleContinueToLanguage() {
    const contactError = validateContactStepFields({ name, phone }, t, demoForm);
    if (contactError) {
      setError(contactError);
      setPendingFocus(contactError === t(demoForm.errorValidationName) ? 'name' : 'phone');
      return;
    }
    setError(null);
    goToStep('language', 'lang');
  }

  /** Enter in a step-1 text input would otherwise natively submit the whole
   * form before consent/language exist on screen — route it through the
   * same gate as "Continuă" instead. */
  function handleContactStepKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleContinueToLanguage();
  }

  // errorNameMsg/errorPhoneMsg/errorConsentMsg moved ABOVE handleSubmit (was
  // below it) so handleSubmit's ADV-20 re-open logic can reference them.
  const errorNameMsg = t(demoForm.errorValidationName);
  const errorPhoneMsg = t(demoForm.errorValidationPhone);
  const errorConsentMsg = t(demoForm.errorConsentRequired);

  // ── Submit ────────────────────────────────────────────────────────────────
  // Validation, error-mapping, the network call, and the auto-navigate check
  // are module-level helpers (see above) — this handler only orchestrates
  // React state, which keeps it out of the cognitive-complexity danger zone.
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const validationError = validateDemoFormFields({ name, phone, consent }, t, demoForm);
    if (validationError) {
      setError(validationError);
      // ADV-20: a submit-time error must never render invisibly inside a
      // collapsed/inert step — re-open the step that owns the failing field
      // and focus it. In normal use this is unreachable (the "Continuă" gate
      // already blocks advancing with a bad name/phone) — it only matters
      // as a defensive re-sync (e.g. state restored some other way).
      if (staged) {
        if (validationError === errorNameMsg) {
          goToStep('contact', 'name');
        } else if (validationError === errorPhoneMsg) {
          goToStep('contact', 'phone');
        } else if (validationError === errorConsentMsg) {
          goToStep('language', 'consent');
        }
      }
      return;
    }

    setSubmitting(true);

    // Create a fresh AbortController for this submit. Abort any prior in-flight
    // request (e.g. rapid double-submit, or morph that triggered two mounts).
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await submitDemoFormRequest(
        {
          name: name.trim(),
          phone: phone.trim(),
          lang: demoLang,
          consent,
          company_website: honeypot, // honeypot — server rejects if non-empty
          attribution: readStoredAttribution(),
        },
        controller.signal
      );

      if (!result.success) {
        setError(t(mapDemoServerError(result.error, demoForm)));
        return;
      }

      const waUrl = result.waUrl ?? '';
      setSuccess({ waUrl });
      trackEvent('form_submit', 'demo_whatsapp');
    } catch (err: unknown) {
      // AbortError = component unmounted mid-fetch — silently ignore.
      // All other errors are user-facing (§C3 inline recoverable message).
      if (isAbortError(err)) return;
      setError(t(botForm.error));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Derive field-level error flags ────────────────────────────────────────
  // (errorNameMsg/errorPhoneMsg/errorConsentMsg now declared above handleSubmit)
  const hasNameError = error === errorNameMsg;
  const hasPhoneError = error === errorPhoneMsg;
  const hasConsentError = error === errorConsentMsg;
  const hasFormError = !!error && !hasNameError && !hasPhoneError && !hasConsentError;

  // R5-P3 LANE L5 no-JS fallback (ADV-11): buildNoscriptHtml (helpers.ts) is SSR/re-render stable — STAGED copy + STATIC_WA_URL only, never live state.
  const noscriptHtml = buildNoscriptHtml(t(stagedCopy.noscript), STATIC_WA_URL);

  // ── Field JSX — written ONCE, shared verbatim between the flat
  // (staged=false) stack and the staged 3-step layout below (Rule #13:
  // shared over duplicated; only the surrounding wrapper/grid/inert plumbing
  // differs between the two modes, never the fields themselves). ──
  const nameField = (
    <div className="wa-demo-field-row space-y-1.5">
      <label htmlFor={nameId} className={cn('block text-sm font-medium', PAPER_INK.secondary.text)}>
        {t(demoForm.nameLabel)}
        <span className={cn('ml-0.5', PAPER_INK.muted.text)} aria-hidden="true">
          *
        </span>
      </label>
      <input
        ref={nameInputRef}
        id={nameId}
        type="text"
        name="name"
        autoComplete="name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setError(null);
        }}
        // Only wired in staged mode: Enter here would otherwise natively
        // submit the whole form before language/consent even exist on
        // screen (ADV-20 validate-before-advance).
        onKeyDown={staged ? handleContactStepKeyDown : undefined}
        // First field in the form (both flat + staged layouts share this JSX,
        // Rule #13) — the natural place to fire the once-per-mount
        // "form_start" telemetry event (contract 3).
        onFocus={handleFirstFieldFocus}
        placeholder={t(demoForm.namePlaceholder)}
        aria-label={t(demoForm.nameLabel)}
        aria-required="true"
        aria-invalid={hasNameError ? 'true' : undefined}
        aria-describedby={hasNameError ? nameErrorId : undefined}
        className={cn(
          'w-full h-12 px-4 py-2.5 rounded-xl text-base',
          PAPER_INK.primary.text,
          `${PAPER_VARIANT.mutedPlaceholderText} border`,
          PAPER_BAND.base.bg,
          getDemoInputBorderClass(hasNameError),
          // R6 form-state law (judge §2.8): outline-based focus -- a stable
          // 1px border in every state, plus an always-reserved (transparent
          // until focused) outline slot so focus never fades in or shifts
          // layout. Replaces the old outline-none focus-visible:ring-1.
          'outline outline-2 outline-transparent outline-offset-2 transition-colors motion-reduce:transition-none',
          hasNameError ? 'focus-visible:outline-red-600' : 'focus-visible:outline-[#FBBF24]'
        )}
      />
      {/* R6 form-state law (judge §2.8): unconditional, reserved-height error
          node -- kills validation reflow; role="alert" only while errored. */}
      <p
        id={nameErrorId}
        role={hasNameError ? 'alert' : undefined}
        aria-live="polite"
        className="mt-1 min-h-[2.5rem] text-sm leading-5 text-red-600"
      >
        {hasNameError ? error : null}
      </p>
    </div>
  );

  const phoneField = (
    <div className="wa-demo-field-row space-y-1.5">
      <label
        htmlFor={phoneId}
        className={cn('block text-sm font-medium', PAPER_INK.secondary.text)}
      >
        {t(demoForm.phoneLabel)}
        <span className={cn('ml-0.5', PAPER_INK.muted.text)} aria-hidden="true">
          *
        </span>
      </label>
      <input
        ref={phoneInputRef}
        id={phoneId}
        type="tel"
        name="phone"
        autoComplete="tel"
        inputMode="tel"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
          setError(null);
        }}
        onKeyDown={staged ? handleContactStepKeyDown : undefined}
        placeholder={t(demoForm.phonePlaceholder)}
        aria-label={t(demoForm.phoneLabel)}
        aria-required="true"
        aria-invalid={hasPhoneError ? 'true' : undefined}
        aria-describedby={hasPhoneError ? phoneErrorId : undefined}
        className={cn(
          'w-full h-12 px-4 py-2.5 rounded-xl text-base',
          PAPER_INK.primary.text,
          `${PAPER_VARIANT.mutedPlaceholderText} border`,
          PAPER_BAND.base.bg,
          getDemoInputBorderClass(hasPhoneError),
          'outline outline-2 outline-transparent outline-offset-2 transition-colors motion-reduce:transition-none',
          hasPhoneError ? 'focus-visible:outline-red-600' : 'focus-visible:outline-[#FBBF24]'
        )}
      />
      <p
        id={phoneErrorId}
        role={hasPhoneError ? 'alert' : undefined}
        aria-live="polite"
        className="mt-1 min-h-[2.5rem] text-sm leading-5 text-red-600"
      >
        {hasPhoneError ? error : null}
      </p>
    </div>
  );

  const consentField = (
    <div className="wa-demo-consent-row space-y-1">
      <label
        htmlFor={consentId}
        className="group relative flex items-start gap-3 cursor-pointer select-none"
      >
        <input
          ref={consentInputRef}
          id={consentId}
          type="checkbox"
          name="consent"
          checked={consent}
          onChange={(e) => {
            setConsent(e.target.checked);
            setError(null);
          }}
          aria-required="true"
          aria-invalid={hasConsentError ? 'true' : undefined}
          aria-describedby={hasConsentError ? consentErrorId : undefined}
          className="sr-only peer"
        />
        <span
          aria-hidden="true"
          className={cn(
            'mt-0.5 w-5 h-5 shrink-0 rounded flex items-center justify-center',
            PAPER_BAND.base.bg,
            'border transition-colors',
            hasConsentError && !consent ? 'border-red-500/60' : PAPER_RULE.hairline.border,
            'peer-checked:bg-black/[0.06] peer-checked:border-black/20',
            // R6 form-state law (judge §2.8): checkbox proxy -> outline
            // equivalent (was peer-focus-visible:ring-2 ring-amber-400
            // ring-offset-1 ring-offset-black).
            'outline outline-2 outline-transparent outline-offset-2',
            'peer-focus-visible:outline-[#FBBF24]'
          )}
        >
          <Check
            className={cn(
              'w-3 h-3 transition-opacity',
              PAPER_INK.primary.text,
              consent ? 'opacity-100' : 'opacity-0'
            )}
            aria-hidden="true"
          />
        </span>
        <span className={cn('text-sm leading-relaxed', PAPER_INK.muted.text)}>
          {t(demoForm.consentLabel)}
          <span className={cn('ml-0.5', PAPER_INK.muted.text)} aria-hidden="true">
            *
          </span>
        </span>
      </label>
      <p
        id={consentErrorId}
        role={hasConsentError ? 'alert' : undefined}
        aria-live="polite"
        className="mt-1 ml-8 min-h-[2.5rem] text-sm leading-5 text-red-600"
      >
        {hasConsentError ? error : null}
      </p>
    </div>
  );

  const langPicker = (
    <div
      id={langGroupId}
      role="radiogroup"
      aria-label={t(demoForm.langPickerLabel)}
      className="wa-demo-lang-toggle flex items-center justify-center"
      onKeyDown={(e) => {
        // Full ARIA radiogroup keyboard pattern (ADV-20): arrows + Home/End,
        // focus follows selection (roving tabindex: the OTHER button is
        // tabIndex=-1, so a subsequent Tab must leave from the NEWLY
        // selected button, not the one that just lost focus).
        const order = ['ro', 'en'] as const;
        const currentIndex = order.indexOf(demoLang);
        let nextLang: 'ro' | 'en' | null = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          nextLang = order[(currentIndex + 1) % order.length];
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          nextLang = order[(currentIndex - 1 + order.length) % order.length];
        } else if (e.key === 'Home') {
          nextLang = order[0];
        } else if (e.key === 'End') {
          nextLang = order[order.length - 1];
        }
        if (!nextLang) return;
        e.preventDefault();
        setDemoLang(nextLang);
        (nextLang === 'ro' ? roLangButtonRef : enLangButtonRef).current?.focus();
      }}
    >
      <div
        className={cn(
          'inline-flex rounded-lg border overflow-hidden p-0.5 gap-0.5',
          PAPER_RULE.hairline.border,
          PAPER_BAND.base.bg
        )}
      >
        {(['ro', 'en'] as const).map((lang) => {
          const isSelected = demoLang === lang;
          return (
            <button
              key={lang}
              ref={lang === 'ro' ? roLangButtonRef : enLangButtonRef}
              type="button"
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => setDemoLang(lang)}
              className={cn(
                'relative px-3 py-1 rounded-md text-xs font-semibold tracking-wide',
                'transition-colors motion-reduce:transition-none',
                // R6 form-state law (judge §2.8): radiogroup buttons -> same
                // outline recipe as the rest of the form (was focus-visible:
                // ring-2 ring-amber-400 ring-offset-1 ring-offset-black).
                'outline outline-2 outline-transparent outline-offset-2',
                'focus-visible:outline-[#FBBF24]',
                isSelected
                  ? cn('bg-black/[0.06]', PAPER_INK.primary.text)
                  : cn(PAPER_INK.secondary.text, PAPER_VARIANT.primaryHoverText, 'hover:bg-black/5')
              )}
            >
              {lang.toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );

  const formErrorNode = (
    <p
      role={hasFormError ? 'alert' : undefined}
      aria-live="polite"
      className="wa-demo-form-error mt-1 min-h-[2.5rem] text-sm leading-5 text-red-600"
    >
      {hasFormError ? error : null}
    </p>
  );

  const submitButtonNode = (
    <button
      type="submit"
      disabled={submitting}
      aria-busy={submitting}
      className={cn(
        'wa-demo-submit-btn',
        LANDING_PRIMARY_CTA_RECIPE,
        'w-full h-12 px-6 py-2.5 rounded-xl',
        'inline-flex items-center justify-center gap-2 font-semibold text-base',
        'disabled:opacity-60 disabled:cursor-not-allowed'
      )}
    >
      {submitting ? (
        <>
          <Loader2
            className="w-5 h-5 shrink-0 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
          />
          <span>{t(botForm.submitting)}</span>
        </>
      ) : (
        <>
          <MessageCircle className="w-5 h-5 shrink-0" aria-hidden="true" />
          {/* compact=true (floating bar): short CTA fits one line; compact=false (attached): full persuasive CTA */}
          <span>{compact ? t(demoForm.submitButtonShort) : t(demoForm.submitButton)}</span>
        </>
      )}
    </button>
  );

  // Step 0 ("Testează-l și tu") + the step1->step2 "Continuă" gate — the two
  // UI atoms unique to the staged flow live in WhatsAppDemoFormStaged.tsx
  // (kept out of this file to stay under the 800-line component cap).
  const ctaStepContent = (
    <StagedCtaCard
      ref={ctaButtonRef}
      headline={t(stagedCopy.cta)}
      line={t(stagedCopy.line)}
      onPress={handleCtaPress}
    />
  );
  const continueButtonNode = (
    <StagedContinueButton label={t(CONTINUE_LABEL)} onPress={handleContinueToLanguage} />
  );

  // ── Form (+ in-place success overlay, §C3) ───────────────────────────────
  // The relative wrapper preserves the form's bounding box on success: the form
  // stays mounted (inert, opacity-0) and the success card overlays it — zero
  // layout shift, 200ms crossfade, persists with no auto-reset.
  return (
    <div className="relative">
      {/* ADV-11 no-JS truth: React-only form (onSubmit, no native action) — a
          no-JS visitor sees it but cannot submit, staged or not. This is a
          PARALLEL escape hatch, not an alternate render of the form below.
          dangerouslySetInnerHTML is required (not a style choice): browsers
          parse <noscript> content as opaque rawtext whenever scripting is
          enabled (every browser that ever runs React hydration), so real JSX
          children here would hydration-mismatch (element nodes expected, one
          text node found). Every interpolated value is content.ts copy or a
          config-sourced URL (escaped anyway — escapeHtmlText above). */}
      <noscript
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- static marketing copy + a config-sourced URL, zero user input; see the block comment above for why <noscript> children cannot be plain JSX here.
        dangerouslySetInnerHTML={{ __html: noscriptHtml }}
      />
      <form
        ref={formRef}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        noValidate
        aria-label={t(demoForm.heading)}
        // inert removes the faded-out form from focus order, AT, and pointer
        // once the success card replaces it (React 19 boolean prop).
        inert={!!success}
        // wa-demo-form: stable semantic hook for the dock's condensed-layout
        // descendant selector (.demo-dock--floating .wa-demo-form in globals.css).
        // Attached = comfortable space-y-4 vertical stack; compact bar overrides via CSS.
        // pointer-events-none while submitting: fields are frozen mid-flight (§C3)
        // with zero layout shift (button label swaps in the same box).
        className={cn(
          'wa-demo-form space-y-4',
          'transition-opacity duration-200 motion-reduce:transition-none',
          success ? 'opacity-0 pointer-events-none' : 'opacity-100',
          submitting && 'pointer-events-none'
        )}
      >
        {/*
         * Visually-hidden honeypot — real users never fill this.
         * aria-hidden removes it from the a11y tree entirely.
         */}
        <div
          aria-hidden="true"
          className="w-px h-px overflow-hidden opacity-0 pointer-events-none"
          style={{ clip: 'rect(0,0,0,0)', position: 'absolute' }}
        >
          <label htmlFor={`${uid}-hp`}>Website</label>
          <input
            id={`${uid}-hp`}
            type="text"
            name="company_website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {staged ? (
          // Staged 3-step machine: all 3 rows stay mounted on ONE grid; the
          // active row's track is 1fr (grows to content), the rest are 0fr
          // — `overflow-hidden` clips them AND (per spec) zeroes their
          // automatic minimum size so 0fr actually reaches zero. Nothing
          // ever unmounts on a step change (top docblock) — only `inert` and
          // the grid fraction move.
          <div
            className={cn(
              'grid transition-[grid-template-rows] motion-reduce:transition-none',
              'duration-[var(--motion-enter)] ease-[var(--ease-enter)]'
            )}
            style={{
              gridTemplateRows: [
                step === 'cta' ? '1fr' : '0fr',
                step === 'contact' ? '1fr' : '0fr',
                step === 'language' ? '1fr' : '0fr',
              ].join(' '),
            }}
          >
            <div className="overflow-hidden" data-step-row="cta" inert={step !== 'cta'}>
              <div className="p-1">{ctaStepContent}</div>
            </div>
            <div className="overflow-hidden" data-step-row="contact" inert={step !== 'contact'}>
              <div className="space-y-4 p-1">
                {nameField}
                {phoneField}
                {continueButtonNode}
              </div>
            </div>
            <div className="overflow-hidden" data-step-row="language" inert={step !== 'language'}>
              <div className="space-y-4 p-1">
                {langPicker}
                {consentField}
                {formErrorNode}
                {submitButtonNode}
              </div>
            </div>
          </div>
        ) : (
          // ── Flat vertical stack (compact/attached — byte-identical shape
          // to before this lane, per r5-defect-map.md §5.2 "additive, does
          // not touch `compact`"). ──
          <>
            {nameField}
            {phoneField}
            {consentField}
            {langPicker}
            {formErrorNode}
            {submitButtonNode}
          </>
        )}

        {/* Reassurance micro-copy DELETED 2026-07-10 round 2: demoForm.reassurance
            is now the identical sentence to botSection.testCard.reassurance,
            already rendered directly above this form's title in SolutionSection —
            keeping both read as an on-the-nose repeat of the same line on the
            same card. content.ts keeps the (unused) key populated so nothing
            else that might still reference it breaks; this is the one render
            call this lane owns and retires. */}
      </form>

      {/* ── Success card — replaces the form IN-PLACE, same bounding box (§C3).
          Bot messages THEM (business-initiated template) — success = "check your
          WhatsApp". House-recipe neutral border + amber check (round 2: no
          green outside the WhatsApp-native bezel); persists, no auto-reset,
          no confetti. Two explicit actions ("Deschide WhatsApp" / "Începe
          trialul CRM") — no auto-navigate on any device (2026-07-10 fix,
          Judge 2 forensic finding 3): the visitor always chooses.
          The aria-live="polite" wrapper is ALWAYS mounted (empty before success) —
          some screen readers skip a live region that arrives already populated on
          first mount, so the region has to exist first and only gain content on
          success (WAI-ARIA live region timing). Before success it is a zero-visual
          -footprint overlay (pointer-events-none, opacity-0, no fill/border) so the
          form underneath stays fully interactive and nothing changes visually. ── */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className={cn(
          'absolute inset-0 overflow-y-auto',
          'flex items-center justify-center text-center',
          'rounded-xl p-6 sm:p-8',
          'transition-opacity duration-200 motion-reduce:transition-none',
          getSuccessOverlayClass(!!success, successVisible)
        )}
      >
        {success && (
          <DemoSuccessCard
            waUrl={success.waUrl}
            headline={t(botForm.success.headline)}
            body={t(botForm.success.body)}
            openWhatsAppLabel={t(demoForm.openWhatsAppButton)}
            crmTrialLabel={t(crmTrialCta)}
          />
        )}
      </div>
    </div>
  );
}
